import React, { useEffect, useState } from "react";
import {
  IResourceComponentsProps,
  useTranslate,
  HttpError,
  useInvalidate,
  useGetIdentity,
} from "@refinedev/core";
import { Edit, useForm } from "@refinedev/antd";
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Switch,
  Divider,
  Drawer,
  Button,
  Tabs,
  InputNumber,
  message,
  Upload,
  Image,
  Flex,
} from "antd";
import type { UploadProps } from "antd";
import {
  SettingOutlined,
  FileTextOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
import { IUpdateTenant, ITenant } from "../../models/tenant";
import { TENANT_SETTINGS_KEY } from "../../authProvider";
import { SuperadminRole } from "../../models/user";
import { TenantGeneralTab } from "./components/TenantGeneralTab";
import { TenantInvoiceSettingsTab } from "./components/TenantInvoiceSettingsTab";
import { TenantPricingTab } from "./components/TenantPricingTab";

export const TenantEdit: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const invalidate = useInvalidate();
  const { data: user } = useGetIdentity();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [textareaRows, setTextareaRows] = useState(20);
  const [activeTab, setActiveTab] = useState("general");

  // Logo states for dark and light logos
  const [logoDarkPreview, setLogoDarkPreview] = useState<string | null>(null);
  const [logoDarkBase64, setLogoDarkBase64] = useState<string | null>(null);
  const [logoLightPreview, setLogoLightPreview] = useState<string | null>(null);
  const [logoLightBase64, setLogoLightBase64] = useState<string | null>(null);

  const { formProps, saveButtonProps, form, queryResult } = useForm<
    ITenant,
    HttpError,
    IUpdateTenant
  >({
    onMutationSuccess: (data) => {
      // Invalidate tenant queries to refetch updated tenant data
      invalidate({ resource: "tenants", invalidates: ["detail", "list"] });

      // Update tenant settings in localStorage if this is the current user's tenant
      const currentTenantId = (user as any)?.tenantId;
      if (data?.data?.id === currentTenantId && data?.data?.settings) {
        const tenantSettings = { ...data.data.settings };
        localStorage.setItem(
          TENANT_SETTINGS_KEY,
          JSON.stringify(tenantSettings),
        );
        // Dispatch custom event to notify theme hook
        window.dispatchEvent(new Event("tenantSettingsUpdated"));
      }
    },
  });

  // Check if user is Superadmin
  const currentUserRoleId = (user as any)?.role?.id;
  const isSuperAdmin =
    currentUserRoleId === SuperadminRole.id ||
    currentUserRoleId === String(SuperadminRole.id) ||
    Number(currentUserRoleId) === SuperadminRole.id;

  // Convert file to base64
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle dark logo file upload
  const handleLogoDarkUpload: UploadProps["beforeUpload"] = async (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error(
        translate(
          "tenants.fields.errors.invalidImageType",
          "Please upload an image file",
        ),
      );
      return Upload.LIST_IGNORE;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(
        translate(
          "tenants.fields.errors.imageTooLarge",
          "Image must be smaller than 2MB",
        ),
      );
      return Upload.LIST_IGNORE;
    }

    try {
      const base64 = await convertToBase64(file);
      setLogoDarkBase64(base64);
      setLogoDarkPreview(base64);
    } catch (error) {
      message.error(
        translate(
          "tenants.fields.errors.uploadFailed",
          "Failed to process image",
        ),
      );
    }

    return false; // Prevent default upload behavior
  };

  // Handle light logo file upload
  const handleLogoLightUpload: UploadProps["beforeUpload"] = async (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error(
        translate(
          "tenants.fields.errors.invalidImageType",
          "Please upload an image file",
        ),
      );
      return Upload.LIST_IGNORE;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(
        translate(
          "tenants.fields.errors.imageTooLarge",
          "Image must be smaller than 2MB",
        ),
      );
      return Upload.LIST_IGNORE;
    }

    try {
      const base64 = await convertToBase64(file);
      setLogoLightBase64(base64);
      setLogoLightPreview(base64);
    } catch (error) {
      message.error(
        translate(
          "tenants.fields.errors.uploadFailed",
          "Failed to process image",
        ),
      );
    }

    return false; // Prevent default upload behavior
  };

  // Remove dark logo
  const handleRemoveLogoDark = () => {
    setLogoDarkBase64(null);
    setLogoDarkPreview(null);
  };

  // Remove light logo
  const handleRemoveLogoLight = () => {
    setLogoLightBase64(null);
    setLogoLightPreview(null);
  };

  // Get the tenant ID being edited
  const editingTenantId = queryResult?.data?.data?.id;

  // Transform settings from JSON string to object before submission
  const handleFinish = async (values: any) => {
    const transformedValues = { ...values };

    // Parse settings JSON string to object if provided
    let settings = {};
    if (
      transformedValues.settings &&
      typeof transformedValues.settings === "string"
    ) {
      // Remove leading/trailing whitespace
      const trimmed = transformedValues.settings.trim();
      if (trimmed === "" || trimmed === "null") {
        settings = {};
      } else {
        try {
          settings = JSON.parse(trimmed);
        } catch (e) {
          // If invalid JSON, keep as is (will be validated by backend)
          console.error("Failed to parse settings JSON:", e);
          settings = {};
        }
      }
    } else if (
      transformedValues.settings &&
      typeof transformedValues.settings === "object"
    ) {
      settings = transformedValues.settings;
    }

    // Remove pricePerCredit from settings if it exists (to avoid duplicates)
    // since we manage it as a direct field on the entity
    if (
      settings &&
      typeof settings === "object" &&
      "pricePerCredit" in settings
    ) {
      delete (settings as any).pricePerCredit;
    }

    transformedValues.settings =
      Object.keys(settings).length > 0 ? settings : undefined;

    // Add logo base64 values
    transformedValues.logoDark = logoDarkBase64 || undefined;
    transformedValues.logoLight = logoLightBase64 || undefined;

    // Call the original onFinish with transformed values
    // pricePerCredit is now kept as a direct field on the entity
    if (formProps.onFinish) {
      return formProps.onFinish(transformedValues);
    }
  };

  useEffect(() => {
    // Convert settings object to JSON string for display
    // Also handle API keys and credentials that might come as objects from the API
    const tenant = queryResult?.data?.data;
    if (tenant) {
      const formValues: any = {};

      // Get pricePerCredit directly from the entity, not from settings
      if (tenant.pricePerCredit !== undefined) {
        formValues.pricePerCredit = tenant.pricePerCredit;
      }

      if (tenant.settings && typeof tenant.settings === "object") {
        try {
          // Create a copy of settings without pricePerCredit for the JSON editor
          // (in case it was previously stored there)
          const settingsForJson = { ...tenant.settings };
          delete settingsForJson.pricePerCredit;

          formValues.settings = JSON.stringify(settingsForJson, null, 2);
        } catch (e) {
          // If already a string, keep it as is
        }
      }

      // Convert object fields to strings if they come as objects from API
      if (tenant.bFlashApiKey) {
        formValues.bFlashApiKey =
          typeof tenant.bFlashApiKey === "string"
            ? tenant.bFlashApiKey
            : String(tenant.bFlashApiKey);
      }
      if (tenant.autotunerMastId) {
        formValues.autotunerMastId =
          typeof tenant.autotunerMastId === "string"
            ? tenant.autotunerMastId
            : String(tenant.autotunerMastId);
      }
      if (tenant.autotunerApiKey) {
        formValues.autotunerApiKey =
          typeof tenant.autotunerApiKey === "string"
            ? tenant.autotunerApiKey
            : String(tenant.autotunerApiKey);
      }
      if (tenant.alientechLogin) {
        formValues.alientechLogin =
          typeof tenant.alientechLogin === "string"
            ? tenant.alientechLogin
            : String(tenant.alientechLogin);
      }
      if (tenant.alientechPassword) {
        formValues.alientechPassword =
          typeof tenant.alientechPassword === "string"
            ? tenant.alientechPassword
            : String(tenant.alientechPassword);
      }
      if (tenant.localApiEndpoint) {
        formValues.localApiEndpoint =
          typeof tenant.localApiEndpoint === "string"
            ? tenant.localApiEndpoint
            : String(tenant.localApiEndpoint);
      }

      if (Object.keys(formValues).length > 0) {
        form.setFieldsValue(formValues);
      }

      // Set logo previews from existing data
      if (tenant.logoDark) {
        setLogoDarkPreview(tenant.logoDark);
        setLogoDarkBase64(tenant.logoDark);
      }
      if (tenant.logoLight) {
        setLogoLightPreview(tenant.logoLight);
        setLogoLightBase64(tenant.logoLight);
      }
    }
  }, [form, queryResult]);

  // Calculate textarea rows based on drawer height
  useEffect(() => {
    if (drawerOpen) {
      const calculateRows = () => {
        // Drawer header is typically ~55px, padding is 48px (24px top + 24px bottom)
        // Each textarea row is approximately 22px
        const headerHeight = 55;
        const padding = 48;
        const rowHeight = 22;
        const availableHeight = window.innerHeight - headerHeight - padding;
        const calculatedRows = Math.floor(availableHeight / rowHeight);
        setTextareaRows(Math.max(calculatedRows, 10)); // Minimum 10 rows
      };

      calculateRows();
      window.addEventListener("resize", calculateRows);
      return () => window.removeEventListener("resize", calculateRows);
    }
  }, [drawerOpen]);

  const tabItems = [
    {
      key: "general",
      label: (
        <Flex align="left" gap={8}>
          <SettingOutlined />
          {translate("tenants.tabs.general", "General")}
        </Flex>
      ),
      children: (
        <TenantGeneralTab
          formProps={formProps}
          onFinish={handleFinish}
          saveButtonProps={saveButtonProps}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          textareaRows={textareaRows}
          form={form}
          logoDarkPreview={logoDarkPreview}
          logoLightPreview={logoLightPreview}
          handleLogoDarkUpload={handleLogoDarkUpload}
          handleLogoLightUpload={handleLogoLightUpload}
          handleRemoveLogoDark={handleRemoveLogoDark}
          handleRemoveLogoLight={handleRemoveLogoLight}
        />
      ),
    },
    {
      key: "invoiceSettings",
      label: (
        <Flex align="center" gap={8}>
          <FileTextOutlined />
          {translate("tenants.tabs.invoiceSettings", "Invoice Settings")}
        </Flex>
      ),
      children: (
        <TenantInvoiceSettingsTab
          tenantId={editingTenantId}
          isSuperAdmin={isSuperAdmin}
          active={activeTab === "invoiceSettings"}
        />
      ),
    },
    {
      key: "pricing",
      label: (
        <Flex align="center" gap={8}>
          <DollarCircleOutlined />
          {translate("tenants.tabs.pricing", "Pricing")}
        </Flex>
      ),
      children: (
        <TenantPricingTab
          tenantId={editingTenantId}
          isSuperAdmin={isSuperAdmin}
          active={activeTab === "pricing"}
        />
      ),
    }
  ];

  return (
    <Edit saveButtonProps={{ style: { display: "none" } }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Edit>
  );
};
